import { Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { SECONDARY_MAX_DECIMAL_DIGITS } from 'business/constants';
import { useRestApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import { formatDurationToReadable } from 'business/shift-report/report/services/timeOperations';
import { useReportControllerV1GetTasksAggregatedByLevel } from 'generated/apiComponents';
import { GetTasksAggregatedByLevelV1Response } from 'generated/apiSchemas';
import { Module_Enum } from 'generated/graphql';
import { createNumberFormatter } from 'technical/format/number';
import { toPercent } from 'technical/utils/converter';

import { CellComponent } from './cell-component';

type DataType =
  GetTasksAggregatedByLevelV1Response['aggregatedTasksByLevel'][number];

interface SummaryTableProps {
  displayedTotalTime: string;
  displayedTotalCriticalTime: string;
}

const SummaryTable = ({
  displayedTotalTime,
  displayedTotalCriticalTime,
}: Readonly<SummaryTableProps>) => {
  const { t } = useTranslation();

  return (
    <Table.Summary.Row>
      <Table.Summary.Cell index={0}>
        <CellComponent level={1}>{t('pages.print.totalTime')}</CellComponent>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={1}>
        <CellComponent level={1}>{displayedTotalTime}</CellComponent>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={2} />
      <Table.Summary.Cell index={3}>
        <CellComponent level={1}>{displayedTotalCriticalTime}</CellComponent>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={4} />
    </Table.Summary.Row>
  );
};

export interface ActivityTableData {
  key: string;
  name: string;
  totalTime: number;
  relativeTime: number;
  totalCriticalTime: number;
  relativeCriticalTime: number;
}

interface ActivityTableProps {
  startDate: string;
  endDate: string;
}

export const ActivityTable = ({ startDate, endDate }: ActivityTableProps) => {
  const { t, language } = useTranslation();
  const { currentConstructionSite } = useAppContext();
  invariant(currentConstructionSite, 'No constructionSite');

  const restHeaders = useRestApiHeaders(Module_Enum.ShiftReport);

  const numberFormatter = createNumberFormatter(
    language,
    SECONDARY_MAX_DECIMAL_DIGITS,
  );

  const { data } = useReportControllerV1GetTasksAggregatedByLevel({
    queryParams: {
      constructionSiteId: currentConstructionSite.id,
      startDate,
      endDate,
    },
    headers: restHeaders,
  });

  const displayedTotalTime = formatDurationToReadable(
    dayjs.duration(data?.totalTime ?? 0, 'minutes').asMinutes(),
  );
  const displayedTotalCriticalTime = formatDurationToReadable(
    dayjs.duration(data?.criticalTotalTime ?? 0, 'minutes').asMinutes(),
  );

  const tableColumnsWithDescription: ColumnsType<DataType> = [
    {
      title: t('pages.print.taskName'),
      dataIndex: 'name',
      render: (node: DataType['name'], record) => (
        <CellComponent level={record.activityLevel}>{node}</CellComponent>
      ),
    },
    {
      title: t('pages.print.taskTime'),
      dataIndex: 'total',
      render: (node: DataType['total'], record) => (
        <CellComponent level={record.activityLevel}>
          {formatDurationToReadable(node)}
        </CellComponent>
      ),
    },
    {
      title: t('pages.print.relativeTime'),
      render: (_: any, record) => (
        <CellComponent
          level={record.activityLevel}
        >{`${numberFormatter.format(toPercent(record.total / (data?.totalTime ?? 1)))}%`}</CellComponent>
      ),
    },
    {
      title: t('pages.print.taskCriticalTime'),
      dataIndex: 'criticalTotal',
      render: (node: DataType['criticalTotal'], record) => (
        <CellComponent level={record.activityLevel}>
          {formatDurationToReadable(node ?? 0)}
        </CellComponent>
      ),
    },
    {
      title: t('pages.print.relativeCriticalTime'),
      render: (_: any, record) => (
        <CellComponent level={record.activityLevel}>
          {`${numberFormatter.format(toPercent((record.criticalTotal ?? 0) / (data?.criticalTotalTime ?? 1)))}%`}
        </CellComponent>
      ),
    },
  ];

  return (
    <Table
      columns={tableColumnsWithDescription}
      dataSource={data?.aggregatedTasksByLevel
        .filter(
          (task) =>
            task.activityLevel <=
            currentConstructionSite.aggregatedReportActivityLevel,
        )
        .map((task) => ({
          ...task,
          key: task.id,
        }))}
      bordered
      pagination={false}
      size="small"
      summary={() => (
        <SummaryTable
          displayedTotalTime={displayedTotalTime}
          displayedTotalCriticalTime={displayedTotalCriticalTime}
        />
      )}
    />
  );
};
